/* eslint-disable react/display-name */
import * as React from 'react'
import * as z from 'zod'

const EDITOR_INPUT_ID = 'list-media-picker-input'

import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  ProvideEditorComponent,
  getMiddleCenterBias,
} from '@glideapps/glide-data-grid'
import { drawImage } from '../../draw-fns'
import Image from 'next/image'
import {
  actionSchema,
  drawAssetActions,
  getSquareBB,
  isOverAction,
  pointIsWithinBB,
} from '../utils'
import { Editor } from './editor'
import {
  mediaBlockSchema,
  imageValueSchema,
  videoValueSchema,
  audioValueSchema,
  fileValueSchema,
} from '@basehub/data/replicache/index'
import { isValidTypeForCategory } from '@basehub/data/replicache/schema/block/valid-mime-types'
import { clsx } from 'clsx'
import { formatBytes } from '~/lib/file-utils'

const baseMediaPickerCellSchema = z.object({
  rounding: z.number().optional(),
  actions: z.array(actionSchema),
  allowedMimeTypes: z.array(z.string()),
  event: z.any().optional(),
  duration: z.string().optional(),
})

export const mediaPickerCellSchema = z.discriminatedUnion('kind', [
  baseMediaPickerCellSchema
    .merge(z.object({ kind: z.literal('media-picker-cell') }))
    .merge(mediaBlockSchema.pick({ value: true })),
  baseMediaPickerCellSchema.merge(
    z.object({
      kind: z.literal('media-picker-pasted-cell'),
      value: z.discriminatedUnion('type', [
        imageValueSchema.omit({
          lastModified: true,
          status: true,
        }),
        videoValueSchema.omit({
          lastModified: true,
          status: true,
        }),
        audioValueSchema.omit({
          lastModified: true,
          status: true,
        }),
        fileValueSchema.omit({
          lastModified: true,
          status: true,
        }),
      ]),
    })
  ),
])

export type MediaPickerCellProps = z.infer<typeof mediaPickerCellSchema> & {
  readonly event?: React.ChangeEvent<HTMLInputElement>
}

export type PastedMediaPickerCellProps = z.infer<
  (typeof mediaPickerCellSchema.options)[1]
>
export type MediaPickerCell = CustomCell<MediaPickerCellProps>

export const MediaPickerRenderer: CustomRenderer<MediaPickerCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is MediaPickerCell => {
    const kind = 'kind' in cell.data && cell.data.kind
    return kind === 'media-picker-cell' || kind === 'media-picker-pasted-cell'
  },
  draw: (args) => {
    const { rect, cell, theme, hoverX, hoverY, highlighted, ctx } = args
    if (cell.data.kind === 'media-picker-pasted-cell') return null

    if (cell.data.value && cell.data.value.status === 'uploaded') {
      if (cell.data.value.type === 'image') {
        drawImage(
          args,
          [cell.data.value.url],
          cell.data.rounding ?? theme.roundingRadius ?? 6,
          cell.contentAlign
        )
      } else {
        const xPad = theme.cellHorizontalPadding
        const drawX = rect.x + xPad

        ctx.font = theme.baseFontFull
        ctx.fillStyle = theme.textDark
        ctx.fillText(
          `${cell.data.value.fileName} (${formatBytes(
            cell.data.value.fileSize
          )})`,
          drawX,
          rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
        )
      }
    }

    const bb = getSquareBB(
      rect.x,
      Math.floor(rect.y + rect.height / 2),
      rect.width * 2
    )
    const hovered =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bb)
        : false

    if (!hovered && !highlighted) return true

    drawAssetActions(args)
    return true
  },
  needsHover: true,
  needsHoverPosition: true,
  provideEditor: () => ({ editor: MediaEditor }),
  onClick: (e) => {
    const clickedAction = isOverAction(e)

    if (e.isDoubleClick) return undefined

    if (!clickedAction) {
      e.preventDefault()
      return undefined
    }

    if (clickedAction !== 'replace') {
      const action = e.cell.data.actions.find((a) => a.name === clickedAction)
      if (action?.onClick) {
        e.preventDefault()
        action.onClick(null, () => {})
      }

      return undefined
    }

    if (clickedAction === 'replace') {
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(
          `input#${EDITOR_INPUT_ID}`
        )
        if (input) input.click()
      })

      return undefined
    }
  },
  onPaste: (toPaste, cell) => {
    let toPasteJson

    try {
      toPasteJson = JSON.parse(toPaste)
    } catch {
      return cell
    }

    const parsedValue =
      mediaPickerCellSchema.options[1].shape.value.safeParse(toPasteJson)
    if (!parsedValue.success) return undefined
    const pastedData = parsedValue.data

    const uri = pastedData.url
    if (!uri) return undefined

    return {
      kind: 'media-picker-pasted-cell',
      value: pastedData,
      actions: cell.actions,
      rounding: cell.rounding,
      allowedMimeTypes: cell.allowedMimeTypes,
      duration: cell.duration,
    }
  },
}

const MediaEditor: ProvideEditorComponent<MediaPickerCell> = (cell) => {
  const _src =
    cell.value.data.kind === 'media-picker-pasted-cell'
      ? ''
      : cell.value.data.value.status === 'uploaded'
        ? cell.value.data.value.url
        : null
  const [src, setSrc] = React.useState<string | null>(_src)
  const mediaRef = React.useRef<HTMLImageElement>(null)
  const mediaDisplayRef = React.useRef<HTMLSpanElement>(null)

  if (cell.value.data.kind === 'media-picker-pasted-cell') return null

  const mediaSrc =
    src ??
    (cell.value.data.value.status === 'uploaded'
      ? cell.value.data.value.url
      : '')

  const imageAlt =
    cell.value.data.value.status === 'uploaded' &&
    cell.value.data.value.type === 'image'
      ? (cell.value.data.value.altText ?? '')
      : ''

  return (
    <Editor
      inputId={EDITOR_INPUT_ID}
      allowedMimeTypes={cell.value.data.allowedMimeTypes}
      isEmpty={cell.value.data.value.status !== 'uploaded'}
      isHiglighted={cell.isHighlighted}
      actions={cell.value.data.actions}
      onFinishedEditing={cell.onFinishedEditing}
      onChange={(e) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (
          isValidTypeForCategory(
            file.type,
            // @ts-expect-error
            Object.keys(cell.value.data.allowedMimeTypes)
          )
        ) {
          if (mediaRef.current) {
            setSrc(URL.createObjectURL(file))
          }

          if (mediaDisplayRef.current) {
            if (file.type.startsWith('video/')) {
              const url = URL.createObjectURL(file)
              const video = document.createElement('video')
              const onLoadedData = () => {
                if (!mediaDisplayRef.current) return
                mediaDisplayRef.current.innerText = `${
                  file.name
                } (${formatBytes(file.size)})`
              }
              video.onloadeddata = onLoadedData
              video.src = url
            } else if (file.type.startsWith('audio/')) {
              const url = URL.createObjectURL(file)
              const audio = new Audio(url)
              const onLoadedData = () => {
                if (!mediaDisplayRef.current) return
                if (!audio.duration) return
                const duration = Math.round(audio.duration)
                const minutes = Math.floor(duration / 60)
                const remainingSeconds = duration % 60
                mediaDisplayRef.current.innerText = `${minutes}:${remainingSeconds}`
              }
              audio.onloadeddata = onLoadedData
            } else if (file.type.startsWith('image/')) {
              // Handled by the image ref
            } else {
              mediaDisplayRef.current.innerText = `${file.name} (${formatBytes(
                file.size
              )})`
            }
          }
        }

        cell.onChange({ ...cell.value, data: { ...cell.value.data, event: e } })
      }}
    >
      {cell.value.data.value.status === 'uploaded' &&
      cell.value.data.value.type === 'image' ? (
        <Image
          ref={mediaRef}
          src={mediaSrc}
          alt={imageAlt}
          className={clsx(
            !src && 'invisible',
            'object-contain select-none pointer-events-none'
          )}
          width={200}
          height={100}
        />
      ) : (
        <span ref={mediaDisplayRef}>
          {cell.value.data.value.status === 'uploaded'
            ? cell.value.data.value.type === 'audio'
              ? cell.value.data.duration
              : `${cell.value.data.value.fileName} (${formatBytes(
                  cell.value.data.value.fileSize
                )})`
            : ''}
        </span>
      )}
    </Editor>
  )
}
